// packages/apps/pro-studio/src/stores/utils/behaviors-vocabulary.ts
//
// PSTUDIO-4167: the single source of truth for what a node's Behaviors column
// may hold. Pure — no store, React or api-services imports — so the CSV parser,
// the paste coercion and the cell renderer can share it without dragging any of
// them into each other.
//
// Confirmed with the backend (Faisal Ahmad, 2026-09-10). The agent compiler and
// the BOT compiler share one `VALID_BEHAVIORS` enum, and a value outside it
// SHOULD fail the compiler's validation as an unrecognized value. Their enum
// holds seven tokens; the two that differ from this list are deliberate:
//
//   * `none` is NOT in it. Pro Studio used to accept `none` on paste and pass
//     it through to the compiler, i.e. the frontend manufactured data the
//     compiler must reject. It is unrecognized here, on purpose.
//   * `out_of_scope` IS in their enum but is INTENT-ONLY in Pro Studio: setting
//     the "NL entry node" writes it into the row's `intent` column. It is in
//     their enum only because the bot compiler historically also accepted it in
//     the behaviors column, and the agent compiler was matched to that. Do not
//     "fix" it back into this list — it would appear in the Behaviors dropdown
//     as a selectable behaviour, which it is not.
//
// Before this module the vocabulary existed in three places that disagreed: the
// UI label map (these six), the paste coercion (these six plus `none` and the
// alias, DROPPING anything else), and CSV import (no vocabulary at all, keeping
// everything verbatim). So paste quietly erased what import quietly published.

export const VALID_BEHAVIORS = [
  'disable_input',
  'inline',
  'multiple_choice',
  'xfer_to_agent',
  'voice_enable_barge_in',
  'is_collapsible',
] as const;

export type ValidBehavior = (typeof VALID_BEHAVIORS)[number];

// Absent from the compiler's enum, but it normalizes onto a token that IS in
// it, so the alias costs nothing and legacy CSVs use it.
const BEHAVIOR_ALIASES: Record<string, ValidBehavior> = {
  transfer_to_agent: 'xfer_to_agent',
};

/**
 * The stored form of a raw token: trimmed, lowercased, aliases resolved.
 *
 * Returns an unrecognized token UNCHANGED rather than dropping it. That is the
 * point of PSTUDIO-4167: the paste path used to filter unknown values out
 * silently, which is data loss dressed up as cleanup. Keeping it is what makes
 * the problem fixable — the compiler already reports an unrecognized behavior
 * against the cell that holds it ("Invalid behavior value 'x' found."), which
 * paints the cell and blocks publish, so the value has to still BE there for
 * that report to point at anything.
 */
export const normalizeBehaviorToken = (raw: string): string => {
  const text = raw.trim().toLowerCase();
  if (text === '') return '';
  // `hasOwnProperty`, not a bare lookup: `BEHAVIOR_ALIASES` is an object
  // literal, so `BEHAVIOR_ALIASES['__proto__']` answers `Object.prototype` and
  // `['constructor']` the `Object` function. The text reaching here is user
  // input — a cell, a pasted clipboard envelope, a hand-edited CSV — and such a
  // value would land in the row as an object and be rejected by `[String!]`,
  // taking the whole upsert chunk with it (PSTUDIO-4081), or render as an
  // object and throw out of the cell (PSTUDIO-4082).
  return Object.prototype.hasOwnProperty.call(BEHAVIOR_ALIASES, text)
    ? BEHAVIOR_ALIASES[text]
    : text;
};

/**
 * The stored form of a whole Behaviors list: every token normalized, blanks
 * dropped, duplicates removed, order preserved.
 *
 * Both text entrances use this rather than mapping `normalizeBehaviorToken`
 * themselves, because the two things it adds are easy to forget and neither is
 * cosmetic:
 *
 *   - **Blanks.** `table-store`'s `defaultRows` seeds a brand-new solution with
 *     `behaviors: ['']`, and both entrances used to filter blanks separately.
 *     Doing it here keeps an empty string from ever being treated as a token —
 *     a blank is not a behavior anyone chose, and anything downstream that
 *     judges tokens would otherwise accuse every new project of one.
 *   - **Duplicates.** `xfer_to_agent,transfer_to_agent` were two distinct
 *     strings before alias resolution and are one token after it. The duplicate
 *     is published twice and gives React duplicate `key`s in the tag list.
 *
 * Accepts `unknown[]` on purpose: a clipboard envelope is cast `as TableRow[]`
 * with no per-field validation, so an element can be any JSON value. Non-string
 * elements are dropped here — their problem is shape, not vocabulary, and the
 * cell renderer already surfaces a desynced value (PSTUDIO-4082).
 */
export const normalizeBehaviorTokens = (raw: unknown[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const element of raw) {
    if (typeof element !== 'string') continue;
    const token = normalizeBehaviorToken(element);
    if (token === '' || seen.has(token)) continue;
    seen.add(token);
    out.push(token);
  }
  return out;
};
